import { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AuthContext } from '@lib/context/AuthContextType';
import { portalRegister, getPortalGoogleUrl } from '@lib/api/portal';
import apiClient from '@lib/api/client';
import type { User } from '@/types/Auth.types';

export const PortalRegisterPage = () => {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get('code') || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [invite, setInvite] = useState<{ email_masked: string; is_reset: boolean; valid: boolean } | null>(null);
  const ctx = useContext(AuthContext)!;
  const navigate = useNavigate();
  const prefilled = !!params.get('code');

  // Confirm the invite up front so the client knows which account this sets up
  // (audit: the form never told them, and the raw 48-char code overflowed on phones)
  useEffect(() => {
    const c = params.get('code');
    if (c && /^[a-f0-9]{48}$/.test(c)) {
      apiClient.get(`/portal/auth/invite-info?code=${c}`)
        .then(r => setInvite(r.data.data))
        .catch(() => {});
    }
  }, [params]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setBusy(true);
    setError('');
    try {
      const result = await portalRegister(code, password, username || undefined);
      ctx.setSession(result.accessToken, result.refreshToken, result.user as User);
      // New clients go through onboarding; employees go straight to portal
      navigate(result.user.role === 'employee' ? '/portal/employee' : '/portal/client/onboarding');
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Registration failed');
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    if (!code.trim()) { setError('Enter your invite code first'); return; }
    sessionStorage.setItem('portal_invite_code', code);
    setBusy(true);
    try {
      const url = await getPortalGoogleUrl();
      window.location.href = url;
    } catch {
      setError('Google sign-in not available');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/icon-192.png" alt="OchrePoint" className="w-16 h-16 rounded-2xl mx-auto mb-3 shadow-sm" />
          <h1 className="text-2xl font-bold text-[#2C1810]">OchrePoint Contracting</h1>
          <p className="text-gray-500 text-sm mt-2">{invite?.is_reset ? 'Set a new password' : 'Create your portal account'}</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            {prefilled && invite ? (
              <div className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm ${invite.valid ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
                <span className="material-symbols-outlined text-lg">{invite.valid ? 'verified' : 'schedule'}</span>
                <span className="min-w-0">
                  {invite.valid
                    ? <>Invite verified for <strong>{invite.email_masked}</strong></>
                    : <>This invite has expired or was already used — <a href="tel:+12505100677" className="underline">call us</a> for a fresh one.</>}
                </span>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invite Code</label>
                <input type="text" value={code} onChange={e => setCode(e.target.value)} required
                  placeholder="Paste the code from your invite email"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder="Optional"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password <span className="text-gray-400 font-normal">(8+ characters)</span></label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required minLength={8}
                  autoComplete="new-password"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40" />
                <button type="button" onClick={() => setShowPw(v => !v)} tabIndex={-1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <span className="material-symbols-outlined text-lg">{showPw ? 'visibility_off' : 'visibility'}</span>
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input type={showPw ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)} required
                autoComplete="new-password"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40" />
            </div>
            <button type="submit" disabled={busy}
              className="w-full bg-[#C77D3F] text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-[#b36e35] disabled:opacity-50">
              {busy ? 'Creating account…' : 'Create Account'}
            </button>
          </form>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center text-xs text-gray-400"><span className="bg-white px-2">or</span></div>
          </div>

          <button onClick={handleGoogle} disabled={busy}
            className="w-full border border-gray-300 rounded-lg py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2">
            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#4285F4" d="M47.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h13.2c-.6 3-2.4 5.6-5 7.3v6h8.1c4.7-4.4 7.2-10.8 7.2-17.6z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.7l-8.1-6c-2.2 1.5-5 2.3-7.8 2.3-6 0-11-4-12.8-9.4H2.9v6.2C6.9 43.4 14.9 48 24 48z"/><path fill="#FBBC05" d="M11.2 29.2c-.5-1.5-.8-3-.8-4.7s.3-3.2.8-4.7v-6.2H2.9C1 17.2 0 20.5 0 24s1 6.8 2.9 9.4l8.3-4.2z"/><path fill="#EA4335" d="M24 9.5c3.3 0 6.3 1.1 8.6 3.3l6.4-6.4C35.9 2.1 30.5 0 24 0 14.9 0 6.9 4.6 2.9 11.6l8.3 6.2c1.8-5.4 6.8-8.3 12.8-8.3z"/></svg>
            Continue with Google
          </button>
        </div>

        <p className="text-center text-sm text-gray-500 mt-4">
          Already have an account?{' '}
          <Link to="/portal/login" className="text-[#C77D3F] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
};
